import { NgClass } from '@angular/common';
import { Component, computed, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputErrorComponent } from '@common/components/input/input-error/input-error.component';
import { InputLabelComponent } from '@common/components/input/input-label/input-label.component';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { CustomDatePipe } from '@common/pipes/date.pipe';
import { environment } from '@environments/environment.base';
import { TranslatePipe } from '@ngx-translate/core';
import { DatePickerComponent, DatePickerCustomMessagesComponent } from '@progress/kendo-angular-dateinputs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { BaseInputComponent } from '../base-input/base-input.component';
import { DateInputConfig } from '../input.type';

/**
 * Date input component.
 * @param format - The format of the date.
 * @param minDate - The minimum date.
 * @param maxDate - The maximum date.
 * @param disabledDates - The disabled dates.
 */
@Component({
    selector: 'app-date-input',
    templateUrl: './date-input.component.html',
    providers: [{ provide: BaseInputComponent, useExisting: DateInputComponent }],
    imports: [
        InputLabelComponent,
        DatePickerComponent,
        NgClass,
        TranslatePipe,
        FormsModule,
        InputErrorComponent,
        TooltipDirective,
        DatePickerCustomMessagesComponent,
        CustomDatePipe
    ]
})
export class DateInputComponent extends BaseInputComponent<DateInput> {
    @Input() format: DateInputConfig['format'];
    @Input() formatPlaceholder: DateInputConfig['formatPlaceholder'] = {
        day: this.translateService.instant('day'),
        month: this.translateService.instant('month'),
        year: this.translateService.instant('year'),
        hour: this.translateService.instant('hour'),
        minute: this.translateService.instant('minute'),
        second: this.translateService.instant('second'),
        millisecond: this.translateService.instant('millisecond')
    };
    @Input() minDate: DateInputConfig['minDate'];
    @Input() maxDate: DateInputConfig['maxDate'];
    @Input() disabledDates: DateInputConfig['disabledDates'];

    defaultDateFormat: string = environment.settings.appControl.dateFormat;

    constructor() {
        super();
        this.getDefaultDateFormat();
    }

    dateValue = computed<Date>(() => {
        if (!this.value()) return null;
        if (this.value() instanceof Date) {
            return this.value() as Date;
        }
        const date = new Date(this.value());
        return isNaN(date.getTime()) ? null : date;
    });

    override onValueChange(value: DateInput) {
        super.onValueChange(value);
        this.value.set(value);
    }

    private getDefaultDateFormat() {
        const settings = this.queryService.queryClient.getQueryData<Setting[]>(['settings']);
        if (!settings) return;
        const defaultDateFormat = settings.find(
            (setting: Setting) => setting.key === SettingKeyCodes.DateFormat
        )?.value;
        if (defaultDateFormat) this.format = this.defaultDateFormat = defaultDateFormat;
    }
}

export type DateInput = Date | string;
